"use client";

import { useState } from "react";
import { createPortal } from "react-dom";
import { Camera } from "lucide-react";
import PhotoLightbox from "@/components/result/PhotoLightbox";
import Photo from "@/components/Photo";
import { recFromSearch } from "@/lib/rec-client";

/**
 * Photo grid for the listing page, every tile opening the same
 * full-screen viewer the listing drawer and the result page use.
 *
 * The grid paints bina's f460x345 card size; the viewer loads the full
 * original. The fifth tile carries the count of the rest; on phones the
 * first photo opens the viewer, which pages through all of them.
 */

const card = (u: string) => u.replace(/\/uploads\/(thumbnail|full)\//, "/uploads/f460x345/");
const full = (u: string) => u.replace(/\/uploads\/(thumbnail|f460x345)\//, "/uploads/full/");

export default function ListingGallery({
  listingId, photos, alt, photoWord = "photo",
}: {
  /** The listing's index id: the viewer reports `listing.gallery.open`. */
  listingId?: string;
  photos: string[];
  alt: string;
  /** "şəkil" / "фото" / "photo": each tile's alt is "<title>, şəkil 2 / 11". */
  photoWord?: string;
}) {
  const [open, setOpen] = useState<number | null>(null);
  if (!photos.length) return null;
  const shown = photos.slice(0, 5);
  const rest = photos.length - shown.length;

  return (
    <div className="space-y-2">
      {/* One tall photo over a row of four: the photos share the top block
          with the details card and match its height (owner, 2026-10-04). */}
      <div className="grid grid-cols-4 gap-1.5 overflow-hidden rounded-2xl">
        {shown.map((src, i) => (
          <button
            key={src}
            type="button"
            onClick={() => setOpen(i)}
            aria-label={`${alt}, ${photoWord} ${i + 1} / ${photos.length}`}
            className={`relative cursor-zoom-in bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-inset ${
              i === 0 ? "col-span-4 aspect-[16/10]" : "hidden aspect-[4/3] sm:block"
            }`}
          >
            <Photo
              src={card(src)}
              alt={`${alt}, ${photoWord} ${i + 1} / ${photos.length}`}
              className="hover:opacity-90"
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : undefined}
            />
            {/* Phones show only the first photo: say how many there are. */}
            {i === 0 && photos.length > 1 && (
              <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-xs font-semibold tabular-nums text-white sm:hidden">
                <Camera className="h-3.5 w-3.5" aria-hidden />
                {photos.length}
              </span>
            )}
            {i === shown.length - 1 && rest > 0 && (
              <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-lg font-bold tabular-nums text-white">
                +{rest}
              </span>
            )}
          </button>
        ))}
      </div>
      {open != null && createPortal(
        <PhotoLightbox
          photos={photos.map((u) => ({ url: full(u) }))}
          initialIndex={open}
          onClose={() => setOpen(null)}
          track={listingId ? { id: listingId, rec: recFromSearch(window.location.search) } : null}
        />,
        document.body,
      )}
    </div>
  );
}
